import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, FormHelperText, Grid, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Divider from '@Components/Divider';
import { FormLabel, FormContent } from '@Components/Form';
import DragDrop from '@Components/File/DragDrop';
import { FileResultType } from '../../../Helper/file';
import { Zone } from '@Reducers/RateUpload/Type';
import { SheetUploadType } from '../type';
import SbButton from '@Components/Button';
import Image from '@Components/Image';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import API from '@Services/apiAxios';
import axios, { AxiosError } from 'axios';
import { DropEvent } from 'react-dropzone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import RateSheetPreviewModal from './RateSheetPreviewModal';
import { ConvertApiResponse } from './RateSheetPreview';
import {
  transformConvertResponseToZones,
  transformExcelToRateObj,
  checkIfFileIsStandardRateSheet,
} from '@Reducers/RateUpload/util';
import { transformExcelToRateObjForCSN } from '@Reducers/RateUpload/utilCSN';
import { exportRateSheetToExcel } from './exportRateSheetExcel';

interface PresignedUrlResponse {
  url: string;
  fields: Record<string, string>;
  key: string;
}

function getFileMimeType(file: File, fallbackData?: FileResultType): string {
  if (file.type) return file.type;
  const name = file.name || fallbackData?.fileName || '';
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (ext === 'xls') return 'application/vnd.ms-excel';
  if (ext === 'csv') return 'text/csv';
  return 'application/octet-stream';
}

function SheetUpload(props: SheetUploadType) {
  const theme = useTheme();
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);

  // Stored convert response and modal visibility state
  const [convertedData, setConvertedData] = useState<ConvertApiResponse | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);

  const carrierAndService = useSelector((state: RootStateType) => state.carrierAndService);
  const alternateCountryNameObj = useSelector((state: RootStateType) => state.global.alternateCountryNameObj);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  // Derive vendor / selectedService from props or carrierAndService state
  const selectedServiceObj = carrierAndService?.selectedServiceList?.find(
    s => s._id === props.service || s._id === props.vendor || s.name === props.vendor || s.name === props.service,
  );
  const selectedService = props.vendor || selectedServiceObj?.name || props.service || '';

  // Derive rateType: string ("parcel" | "document" or selected value)
  let selectedRateType = '';
  if (typeof props.rateType === 'boolean') {
    selectedRateType = props.rateType ? 'document' : 'parcel';
  } else if (typeof props.rateType === 'string' && props.rateType) {
    selectedRateType = props.rateType.toLowerCase();
  }

  const isUploadDisabled = !selectedService || !selectedRateType || loading;

  const onFileChange = useCallback(
    async (
      _data: FileResultType[],
      event?: DropEvent | React.ChangeEvent<HTMLInputElement>,
      acceptedFiles?: File[],
    ) => {
      setError('');
      setConvertedData(null);
      setIsPreviewOpen(false);

      // Validation: Prevent upload unless both Service* and rateType are selected
      if (!selectedService || !selectedRateType) {
        if (!selectedService && !selectedRateType) {
          setError('Please select Service and Rates Type before uploading.');
        } else if (!selectedService) {
          setError('Please select Service before uploading.');
        } else {
          setError('Please select Rates Type before uploading.');
        }
        return;
      }

      let file: File | undefined = acceptedFiles?.[0];
      if (!file && event) {
        if ('target' in event && event.target && 'files' in event.target) {
          file = (event.target as HTMLInputElement).files?.[0];
        } else if ('dataTransfer' in event && event.dataTransfer && 'files' in event.dataTransfer) {
          file = event.dataTransfer.files?.[0];
        }
      }

      if (!file) {
        setError('No file selected.');
        return;
      }

      const currentFileName = file.name || _data?.[0]?.fileName || '';
      const currentMimeType = getFileMimeType(file, _data?.[0]);

      setFileName(currentFileName);
      setLoading(true);

      // ==========================================
      // Format Detection: Check if file matches existing standard / sample format
      // ==========================================
      const isStandardFormat = await checkIfFileIsStandardRateSheet(file);

      if (isStandardFormat) {
        // ==========================================
        // Case 1: Standard Sample Rate Sheet Format
        // ALWAYS use existing/non-AI parser flow.
        // NEVER call AI, even if the parser encounters an error.
        // ==========================================
        try {
          const fileEvent =
            event && 'target' in event && (event.target as HTMLInputElement)?.files?.length
              ? (event as React.ChangeEvent<HTMLInputElement>)
              : ({ target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>);

          const parseFn = name === 'Cargo Solutions Network' ? transformExcelToRateObjForCSN : transformExcelToRateObj;
          const parsedData = await parseFn(fileEvent, props.rateApplyFor, alternateCountryNameObj, props.maxWeight);

          if (parsedData?.zone && Array.isArray(parsedData.zone) && parsedData.zone.length > 0) {
            const sheetData: Zone[] = parsedData.zone;
            if (props.setSheet) {
              props.setSheet(sheetData);
            }
            props.handleChange(sheetData);
            setError('');
            setConvertedData(null);
            setIsPreviewOpen(false);
          } else {
            setError('Failed to parse rate sheet.');
          }
        } catch (oldParserErr) {
          console.error('Standard rate sheet parsing error:', oldParserErr);
          const errObj = oldParserErr as { errorMsg?: string; msg?: string; message?: string };
          const errMsg = errObj?.errorMsg || errObj?.msg || errObj?.message || 'Failed to parse rate sheet.';
          setError(errMsg);
          setConvertedData(null);
          setIsPreviewOpen(false);
        } finally {
          setLoading(false);
        }
        return; // NEVER call AI when the sheet matches the existing format
      }

      // ==========================================
      // Case 2: Different / Non-Standard Rate Sheet Format
      // Call existing AI parsing flow (upload-url -> S3 -> convert -> preview modal)
      // ==========================================
      let uploadUrlData: PresignedUrlResponse;

      // Step 1: Get S3 Presigned Upload URL
      try {
        const uploadUrlResponse = await API.post<PresignedUrlResponse | { data: PresignedUrlResponse }>(
          '/speedboxbot/rate-sheet/upload-url',
          {
            fileName: currentFileName,
            mimeType: currentMimeType,
          },
        );

        const responseData = uploadUrlResponse.data;
        if (responseData && typeof responseData === 'object' && 'data' in responseData) {
          uploadUrlData = (responseData as { data: PresignedUrlResponse }).data;
        } else {
          uploadUrlData = responseData as PresignedUrlResponse;
        }

        if (!uploadUrlData?.url || !uploadUrlData?.fields || !uploadUrlData?.key) {
          throw new Error('Invalid upload URL response from server.');
        }
      } catch (err) {
        console.error('Failed to get S3 upload URL:', err);
        const axiosErr = err as AxiosError<{ msg?: string }>;
        const errMsg = axiosErr?.response?.data?.msg || axiosErr?.message || 'Failed to get upload URL.';
        setError(errMsg);
        setLoading(false);
        return; // Stop the flow
      }

      // ==========================================
      // Step 2: Upload the Actual File to S3
      // ==========================================
      try {
        const formData = new FormData();
        Object.entries(uploadUrlData.fields).forEach(([key, value]) => {
          formData.append(key, String(value));
        });
        formData.append('file', file);

        await axios.post(uploadUrlData.url, formData);
      } catch (err) {
        console.error('Failed to upload file to S3:', err);
        const axiosErr = err as AxiosError<{ msg?: string }>;
        const errMsg = axiosErr?.response?.data?.msg || axiosErr?.message || 'Failed to upload file to S3.';
        setError(errMsg);
        setLoading(false);
        return; // Stop the flow
      }

      // ==========================================
      // Step 3: Convert the Uploaded Rate Sheet
      // ==========================================
      try {
        const convertPayload = {
          vendor: selectedService,
          s3Key: uploadUrlData.key,
          rateType: selectedRateType,
        };

        const convertResponse = await API.post<ConvertApiResponse>('/speedboxbot/rate-sheet/convert', convertPayload);

        console.log('Rate sheet conversion response:', convertResponse);

        const responseData = (convertResponse?.data?.data || convertResponse?.data) as ConvertApiResponse;
        setConvertedData(responseData);
        setIsPreviewOpen(true); // Automatically open preview modal

        // Transform response into the exact Zone[] format expected by final submission API
        const transformedZones: Zone[] = transformConvertResponseToZones(responseData, alternateCountryNameObj);

        console.log('Transformed rate-sheet data for submission:', transformedZones);

        if (transformedZones && transformedZones.length > 0) {
          if (props.setSheet) {
            props.setSheet(transformedZones);
          }
          props.handleChange(transformedZones);
        }

        setError('');
      } catch (err) {
        console.error('Failed to convert rate sheet:', err);
        const axiosErr = err as AxiosError<{ msg?: string }>;
        const errMsg = axiosErr?.response?.data?.msg || axiosErr?.message || 'Failed to convert rate sheet.';
        setError(errMsg);
      } finally {
        setLoading(false);
      }
    },
    [selectedService, selectedRateType, props, name, alternateCountryNameObj],
  );

  const onDownloadSampleSheet = () => {
    let link = undefined;
    if (props.rateApplyFor === 'rate-sheet') {
      link = 'https://s3.ap-south-1.amazonaws.com/speedbox-static-file-2.0/Sample%2BBase%2BRate%2BSheet+(1).xlsx';
    } else if (props.rateApplyFor === 'zone-wise') {
      link = 'https://s3.ap-south-1.amazonaws.com/speedbox-static-file-2.0/Sample%2BZone%2BWise%2BRate%2BSheet.xlsx';
    } else {
      link = 'https://s3.ap-south-1.amazonaws.com/speedbox-static-file-2.0/Sample%2BBase%2BRate%2BSheet+(1).xlsx';
    }
    if (link) window.open(link, '_blank');
  };

  return (
    <Grid item container xs={12} alignItems="center">
      <Grid item xs={12} md={4} lg={3} mb={{ xs: 1 }}>
        <FormLabel>Rate Sheet*</FormLabel>
      </Grid>
      <Grid item xs={12} md={8} lg={9}>
        <FormContent>
          <DragDrop
            readType="xls"
            disabled={isUploadDisabled}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            subText={loading ? 'Processing rate sheet...' : fileName || 'only xls and xlsx file allowed'}
            onChange={onFileChange}
          />
          {loading && (
            <Grid container alignItems="center" spacing={1} className="mt-1">
              <Grid item>
                <CircularProgress size={16} />
              </Grid>
              <Grid item>
                <FormHelperText>Uploading and converting rate sheet, please wait...</FormHelperText>
              </Grid>
            </Grid>
          )}
          {(props.error || error) && (
            <FormHelperText error className="mt-1">
              {props.error || error}
            </FormHelperText>
          )}
        </FormContent>
        <SbButton
          variant="text"
          className="mt-1"
          onClick={onDownloadSampleSheet}
          startIcon={
            <Image
              src={'/images/upload_document.svg'}
              alt="Download"
              width={35}
              height={35}
              style={{ transform: 'rotate(180deg)' }}
            />
          }
        >
          Download Sample File
        </SbButton>
      </Grid>

      {/* Success banner and Reopen Preview button (when convert API response is available) */}
      {convertedData && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
              backgroundColor: alpha(theme.palette.success.main, 0.04),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 26 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                  Rate Sheet Converted Successfully ✓
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {convertedData.vendor || selectedService || 'Vendor'} •{' '}
                  {convertedData.rateType || selectedRateType || 'Parcel'} • {convertedData.zones?.length || 0}{' '}
                  destinations mapped
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <SbButton
                variant="outlined"
                size="small"
                onClick={() => exportRateSheetToExcel(convertedData)}
                startIcon={<DownloadIcon />}
              >
                Download Excel
              </SbButton>
              <SbButton
                variant="contained"
                size="small"
                onClick={() => setIsPreviewOpen(true)}
                startIcon={<VisibilityOutlinedIcon />}
              >
                View Rate Sheet Preview
              </SbButton>
            </Box>
          </Box>
        </Grid>
      )}

      {/* Rate Sheet Preview Modal */}
      <RateSheetPreviewModal open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} data={convertedData} />

      <Divider className="my-2" />
    </Grid>
  );
}

export default SheetUpload;
